import type { MutableRefObject } from 'react'
import {
  computeRotationAngle,
  computeRotationAngleSync,
} from '@/lib/boundaries/bounding-box-boundary'
import { resolveSnappedRotationDegrees, snapRotationAngle } from '@/lib/guides/smart-guides'
import { getOtherObjectRotations } from '@/lib/guides/rotation-snap-helpers'
import { normalizeRotationDegrees, shortestDeltaDegrees } from '@/lib/transforms/geometry'
import {
  collectChangedMultiRotateCommits,
  computeMultiRotatePreview,
} from '@/lib/transforms/multi-rotation'
import type { InteractionState } from '../types/interaction-state'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerProps } from '../types/shape-manager-props'
import { MultiRotateInteraction, RotateGestureDeps, RotatePointer } from './rotate-gesture'

export interface MultiRotateCommitDeps extends Pick<
  RotateGestureDeps,
  'getShapes' | 'smartGuidesEnabled' | 'updateShape' | 'setMultiSelectChrome'
> {
  pendingRotateChromeBeforeRef: MutableRefObject<MultiSelectChrome | null>
  onBatchShapesCommitted: ShapeManagerProps['onBatchShapesCommitted']
}

/** Pointer-up of a multi-select rotate: apply the final angle, then commit every changed shape. */
export function commitMultiRotate(
  interaction: MultiRotateInteraction,
  event: Pick<PointerEvent, 'clientX' | 'clientY'>,
  {
    getShapes,
    smartGuidesEnabled,
    updateShape,
    setMultiSelectChrome,
    pendingRotateChromeBeforeRef,
    onBatchShapesCommitted,
  }: MultiRotateCommitDeps
): void {
  if (
    interaction.multiChromeRotationStartDeg !== undefined &&
    interaction.rotationStartAngle !== undefined &&
    interaction.overlayRect
  ) {
    const ox = interaction.overlayRect
    const cx = ox.left + ox.width / 2
    const cy = ox.top + ox.height / 2
    const angle = Math.atan2(event.clientY - cy, event.clientX - cx)
    const deltaRad = angle - interaction.rotationStartAngle
    const startChrome = interaction.multiChromeRotationStartDeg
    const mouseDeltaDeg = (deltaRad * 180) / Math.PI
    const rawChromeDeg = normalizeRotationDegrees(startChrome + mouseDeltaDeg)

    let targetChromeDeg = rawChromeDeg
    let shapeDeltaDeg = mouseDeltaDeg

    if (smartGuidesEnabled) {
      const multiChromeRotateExcludeIds = new Set(
        interaction.multiRotateInitials ? Object.keys(interaction.multiRotateInitials) : []
      )
      const multiChromeRotateOtherAngles = getOtherObjectRotations(
        multiChromeRotateExcludeIds,
        getShapes(),
        []
      )
      const snap = snapRotationAngle(rawChromeDeg, 5, multiChromeRotateOtherAngles)

      if (snap.didSnap) {
        targetChromeDeg = snap.snapped
        shapeDeltaDeg = shortestDeltaDegrees(startChrome, targetChromeDeg)
      }
    }

    const updates = computeMultiRotatePreview({
      initials: interaction.multiRotateInitials,
      pivot: interaction.rotatePivotLocal,
      deltaDegrees: shapeDeltaDeg,
    })

    for (const [id, update] of Object.entries(updates)) {
      updateShape(id, update)
    }

    setMultiSelectChrome((prev) =>
      prev
        ? {
            ...prev,
            rotationDeg: targetChromeDeg,
          }
        : prev
    )
  }

  const currentById = Object.fromEntries(
    getShapes().map((shape) => [
      shape.id,
      { x: shape.x, y: shape.y, rotation: shape.rotation ?? 0 },
    ])
  )
  const commits = collectChangedMultiRotateCommits({
    initials: interaction.multiRotateInitials,
    currentById,
  })
  const items: BatchShapeTransformItem[] = commits.map((item) => {
    const shape = getShapes().find((s) => s.id === item.id)

    return {
      shapeId: item.id,
      shape,
      before: item.before,
      after: item.after,
    }
  })

  if (items.length > 0) {
    if (
      interaction.multiChromePivotStart &&
      interaction.multiChromeRotationStartDeg !== undefined &&
      interaction.multiChromeSizeStart
    ) {
      pendingRotateChromeBeforeRef.current = {
        pivotX: interaction.multiChromePivotStart.x,
        pivotY: interaction.multiChromePivotStart.y,
        width: interaction.multiChromeSizeStart.width,
        height: interaction.multiChromeSizeStart.height,
        rotationDeg: interaction.multiChromeRotationStartDeg,
      }
    }

    onBatchShapesCommitted?.(items, 'rotate')
  }
}

/** Pointer-up of a single-shape rotate: the final (snapped) angle and the WASM resync inputs. */
export function computeSingleRotateCommit(
  interaction: InteractionState & { overlayRect: DOMRect },
  latest: RotatePointer,
  { getShapes, smartGuidesEnabled }: Pick<RotateGestureDeps, 'getShapes' | 'smartGuidesEnabled'>
): {
  rotationDeg: number
  params: Parameters<typeof computeRotationAngle>[0]
  snapped: boolean
} {
  const cx = interaction.overlayRect.left + interaction.overlayRect.width / 2
  const cy = interaction.overlayRect.top + interaction.overlayRect.height / 2
  const params = {
    centerX: cx,
    centerY: cy,
    mouseX: latest.clientX,
    mouseY: latest.clientY,
    startAngle: interaction.rotationStartAngle,
    initialRotation: interaction.initial.rotation ?? 0,
  }
  const result = computeRotationAngleSync(params)
  const commitRotateExcludeIds = new Set([interaction.shapeId])
  const commitRotateOtherAngles = getOtherObjectRotations(commitRotateExcludeIds, getShapes(), [])
  const resolved = resolveSnappedRotationDegrees(result.degrees, {
    shiftKey: latest.shiftKey,
    smartGuidesEnabled,
    otherAngles: commitRotateOtherAngles,
  })
  const rotationDeg = resolved.degrees

  return { rotationDeg, params, snapped: resolved.didShiftSnap || resolved.didSmartGuideSnap }
}
